const serviceKeys = Object.freeze([
  'classes',
  'assignment_collections',
  'students_per_class',
  'teaching_files',
  'ai_requests_monthly',
]);

const billingPath = '/users/~settings/billing/';

function safeBillingUrl(value, base = window.location.href) {
  try {
    const url = new URL(value || billingPath, base);
    if (url.origin !== window.location.origin || url.pathname !== billingPath) {
      return null;
    }
    return url;
  } catch (_error) {
    return null;
  }
}

function labelsFor(document) {
  const source = document.querySelector('[data-quota-upgrade-messages]');
  if (!source) return null;
  const serviceNames = Object.fromEntries(serviceKeys.map((key) => [
    key,
    source.dataset[`service${key.split('_').map((part) => part[0].toUpperCase() + part.slice(1)).join('')}`],
  ]));
  return {
    heading: source.dataset.heading,
    detail: source.dataset.detail,
    body: source.dataset.body,
    action: source.dataset.action,
    close: source.dataset.close,
    closeLabel: source.dataset.closeLabel,
    recommendation: source.dataset.recommendation,
    atFreeMax: source.dataset.atFreeMax,
    serviceNames,
  };
}

function showModal({ service, used, limit, recommendedPlan, url, document }) {
  let dialog = document.querySelector('[data-quota-upgrade-dialog]');
  if (!dialog) {
    dialog = document.createElement('dialog');
    dialog.className = 'pricing-dialog';
    dialog.dataset.quotaUpgradeDialog = 'true';
    dialog.setAttribute('aria-labelledby', 'quota-upgrade-title');
    dialog.innerHTML = `
      <div class="pricing-dialog-content">
        <button type="button" class="btn-close" data-quota-upgrade-close
          aria-label="Close"></button>
        <h2 id="quota-upgrade-title"></h2>
        <p data-quota-upgrade-detail></p>
        <p data-quota-upgrade-recommendation hidden></p>
        <p data-quota-upgrade-manual-retry></p>
        <a class="btn btn-primary" data-quota-upgrade-link></a>
        <button type="button" class="btn btn-outline-secondary"
          data-quota-upgrade-close></button>
      </div>`;
    document.body.append(dialog);
    dialog.querySelectorAll('[data-quota-upgrade-close]').forEach((button) => {
      button.addEventListener('click', () => dialog.close());
    });
    dialog.addEventListener('close', () => dialog.returnFocus?.focus());
  }

  const messages = labelsFor(document);
  const serviceLabel = messages?.serviceNames[service];
  if (!messages || !serviceLabel) return false;
  const returnFocus = document.activeElement;
  dialog.returnFocus = returnFocus;
  dialog.querySelector('#quota-upgrade-title').textContent = messages.heading;
  dialog.querySelector('[data-quota-upgrade-detail]').textContent = messages.detail
    .replace('{used}', String(used))
    .replace('{limit}', String(limit))
    .replace('{service}', serviceLabel);
  dialog.querySelector('[data-quota-upgrade-manual-retry]').textContent = messages.body;
  const recommendation = dialog.querySelector('[data-quota-upgrade-recommendation]');
  if (typeof recommendedPlan === 'string' && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(recommendedPlan)) {
    const label = recommendedPlan.replace(/(^|-)([a-z])/g, (_match, prefix, letter) => (
      `${prefix}${letter.toUpperCase()}`
    ));
    recommendation.textContent = messages.recommendation.replace('{plan}', label);
    recommendation.hidden = false;
  } else {
    const currentPlan = document.querySelector('[data-quota-error]')?.dataset.currentPlan;
    if (currentPlan === 'max') {
      recommendation.textContent = messages.atFreeMax;
      recommendation.hidden = false;
    } else {
      recommendation.textContent = '';
      recommendation.hidden = true;
    }
  }
  const link = dialog.querySelector('[data-quota-upgrade-link]');
  link.href = url.href;
  link.textContent = messages.action;
  dialog.querySelector('.btn-close').setAttribute('aria-label', messages.closeLabel);
  dialog.querySelectorAll('[data-quota-upgrade-close]:not(.btn-close)').forEach((button) => {
    button.textContent = messages.close;
  });
  dialog.showModal();
  dialog.querySelector('[data-quota-upgrade-close]')?.focus();
  return true;
}

/** Present a safe, manual-retry upgrade route for an authoritative quota error. */
export function showUpgradePrompt({
  service,
  used,
  limit,
  recommendedPlan,
  upgradeUrl,
  mode = 'modal',
} = {}) {
  if (!serviceKeys.includes(service)) return false;
  if (
    !Number.isFinite(Number(used))
    || Number(used) < 0
    || !Number.isFinite(Number(limit))
    || Number(limit) < 1
  ) return false;
  const document = window.document;
  const url = safeBillingUrl(upgradeUrl);
  if (!url) return false;

  const returnTo = `${window.location.pathname}${window.location.search}`;
  url.searchParams.set('service', service);
  if (returnTo.startsWith('/') && !returnTo.startsWith('//')) {
    url.searchParams.set('return_to', returnTo);
  }

  if (mode === 'redirect') {
    window.location.assign(url.href);
    return true;
  }
  if (mode !== 'modal') return false;
  return showModal({ service, used, limit, recommendedPlan, url, document });
}

/** Attach the reusable prompt to server-rendered quota errors and app events. */
export function initQuotaUpgrade(rootElement = document) {
  const root = rootElement.documentElement || rootElement;
  if (root.dataset?.quotaUpgradeInitialized === 'true') return;
  if (root.dataset) root.dataset.quotaUpgradeInitialized = 'true';

  rootElement.querySelectorAll('[data-quota-error]').forEach((error) => {
    showUpgradePrompt({
      service: error.dataset.service,
      used: error.dataset.used,
      limit: error.dataset.limit,
      recommendedPlan: error.dataset.recommendedPlan,
      upgradeUrl: error.dataset.upgradeUrl,
      mode: error.dataset.mode || 'modal',
    });
  });
  rootElement.addEventListener('billing:quota-exceeded', (event) => {
    showUpgradePrompt(event.detail || {});
  });
}

export { safeBillingUrl };
